import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Standard SaaS Terms of Use (Portuguese Brazil).
 * Covers: LGPD, cancellation, 7-day refund window, acceptable use, jurisdiction (São Paulo).
 * Publicly linked from Landing footer and Signup checkbox.
 */
export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm hover:underline" data-testid="terms-back-link">
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
          <div className="text-xs uppercase tracking-[0.3em] text-foreground/60">Documento Legal</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16" data-testid="terms-page">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-2">v1.0 — Vigente</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-none">
            Termos de Uso
          </h1>
          <p className="mt-4 text-sm text-foreground/60">
            Última atualização: {new Date().toLocaleDateString("pt-BR")}
          </p>
        </div>

        <div className="prose prose-neutral max-w-none space-y-6 text-[15px] leading-relaxed">
          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">1. Aceitação dos Termos</h2>
            <p>
              Ao criar uma conta, contratar ou utilizar os serviços do <strong>Fila Inteligente</strong> (&quot;plataforma&quot;),
              você declara ter lido, compreendido e concordado integralmente com estes Termos de Uso e com a nossa
              Política de Privacidade. Caso não concorde, não utilize a plataforma.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">2. Descrição do Serviço</h2>
            <p>
              A plataforma oferece um sistema web multi-tenant de gerenciamento de filas e chamada de senhas para
              estabelecimentos comerciais, clínicas, órgãos públicos e afins, com painéis administrativos, operador,
              exibição em TV, integração com mídia (YouTube, MP4, imagens, RSS) e chamada por voz (TTS).
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">3. Cadastro, Conta e Segurança</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>O usuário compromete-se a fornecer informações verdadeiras, completas e atualizadas.</li>
              <li>É responsabilidade exclusiva do titular a guarda das credenciais de acesso (e-mail e senha).</li>
              <li>É proibido compartilhar a conta com terceiros não autorizados pela mesma empresa.</li>
              <li>O usuário deve notificar imediatamente qualquer suspeita de uso não autorizado da conta.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">4. Planos, Pagamento e Renovação</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>O trial gratuito tem duração de <strong>14 dias corridos</strong>, sem exigência de cartão de crédito.</li>
              <li>Após o trial, o acesso continua mediante contratação do plano vigente, cobrado mensalmente.</li>
              <li>Os pagamentos são processados por gateways externos (ex.: Stripe). Não armazenamos dados de cartão em nossos servidores.</li>
              <li>A não renovação da assinatura resulta em modo somente-leitura: as configurações permanecem, mas novas senhas não podem ser chamadas até a regularização.</li>
              <li>Os preços podem ser reajustados com aviso prévio de 30 dias, respeitando os contratos vigentes.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">5. Cancelamento e Reembolso</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>O cancelamento pode ser feito a qualquer momento pelo painel do administrador ou por solicitação ao suporte.</li>
              <li>
                Nos termos do art. 49 do Código de Defesa do Consumidor, o usuário tem direito ao <strong>reembolso
                integral</strong> em caso de arrependimento, desde que solicitado em até <strong>7 (sete) dias corridos</strong> após
                a contratação.
              </li>
              <li>Após esse prazo, não há reembolso proporcional do período já iniciado, mas o acesso permanece válido até o fim do ciclo pago.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">6. Uso Aceitável</h2>
            <p>É expressamente proibido:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Utilizar a plataforma para atividades ilícitas, fraudulentas ou que violem direitos de terceiros.</li>
              <li>Exibir conteúdo de mídia obsceno, discriminatório, com apologia à violência ou que infrinja direitos autorais.</li>
              <li>Tentar acessar contas de terceiros, decompilar, aplicar engenharia reversa ou explorar vulnerabilidades da plataforma.</li>
              <li>Automatizar chamadas por meio de bots ou realizar sobrecarga intencional das APIs.</li>
            </ul>
            <p className="mt-3">O descumprimento pode gerar suspensão imediata da conta sem reembolso.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">7. Proteção de Dados (LGPD)</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Coletamos apenas os dados necessários à operação do serviço: nome, e-mail, telefone/WhatsApp,
                nome da empresa e dados operacionais das filas.
              </li>
              <li>
                Os dados são tratados conforme a <strong>Lei nº 13.709/2018 (LGPD)</strong>, com finalidade legítima e
                base legal no cumprimento do contrato e legítimo interesse.
              </li>
              <li>O titular pode a qualquer momento solicitar acesso, correção, portabilidade ou exclusão dos seus dados.</li>
              <li>Os dados são armazenados em infraestrutura segura, com criptografia em trânsito (HTTPS) e senhas armazenadas com hash bcrypt.</li>
              <li>Não vendemos dados pessoais a terceiros. Compartilhamento ocorre apenas com processadores de pagamento e provedores de infraestrutura estritamente necessários.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">8. Propriedade Intelectual</h2>
            <p>
              Toda a plataforma, código-fonte, marca, layout, textos e componentes visuais são de propriedade
              exclusiva do Fila Inteligente ou de seus licenciantes. O usuário recebe apenas licença de uso limitada,
              não exclusiva e intransferível durante a vigência da contratação.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">9. Disponibilidade e Suporte</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Meta de disponibilidade (SLA): 99% do tempo mensal, excluídas janelas de manutenção programadas.</li>
              <li>Suporte por e-mail em dias úteis. Planos com suporte prioritário são divulgados na página comercial.</li>
              <li>Não garantimos disponibilidade em caso de falhas de terceiros (provedores de internet, gateways de pagamento, YouTube, etc.).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">10. Limitação de Responsabilidade</h2>
            <p>
              A plataforma é fornecida &quot;como está&quot;. Não nos responsabilizamos por lucros cessantes, perda de dados
              decorrente de mau uso, ou por prejuízos indiretos. Nossa responsabilidade máxima está limitada ao
              valor efetivamente pago pelo usuário nos 12 meses anteriores ao evento.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">11. Alterações destes Termos</h2>
            <p>
              Podemos atualizar estes Termos periodicamente. Alterações materiais serão comunicadas por e-mail
              com no mínimo 15 dias de antecedência. O uso continuado da plataforma após a atualização implica
              aceitação da nova versão.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-black tracking-tight mt-8 mb-3">12. Foro e Legislação Aplicável</h2>
            <p>
              Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o <strong>foro da
              Comarca de São Paulo/SP</strong> para dirimir quaisquer controvérsias, com renúncia expressa a qualquer outro,
              por mais privilegiado que seja.
            </p>
          </section>

          <section className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-foreground/60">
              Dúvidas? Fale conosco pelo canal de suporte informado no painel administrativo.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
